import { FileDownloadOutlined } from '@mui/icons-material'
import { Skeleton } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import { handleException } from 'clients/exception-handling'
import { deriveCustomBreakdowns } from 'features/charts-v2/derive-custom-breakdowns'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { useTopBar } from 'features/project/partials/project-layout/use-layout'
import { useProjectStore } from 'features/project/store'
import { ChangesSinceDetection } from 'features/trend-detection/components/changes-since-detection'
import {
  BreakdownSection,
  VerbatimsSection,
} from 'features/trend-detection/components/DetailPageSections'
import {
  ElevationLevelPopover,
  type TierConfig,
} from 'features/trend-detection/components/ElevationLevelPopover'
import {
  CardActions,
  CardHeader,
  CardIconButton,
  CardTitle,
  ChartModeButton,
  ChartModeButtonGroup,
  ContentWrapper,
  DetectionBadgeRow,
  DetectionLabels,
  DetectionTitle,
  InsightDescription,
  InsightHeader,
  InsightTitle,
  PageContainer,
  PageShell,
  SectionCard,
} from 'features/trend-detection/components/insight-detail-styles'
import { InsightDetailSubHeader } from 'features/trend-detection/components/InsightDetailSubHeader'
import {
  EMERGENCE_BOX_BG,
  EMERGENCE_BOX_BORDER,
  EMERGENCE_COLORS,
  EMERGENCE_ICON_COLOR,
  EMERGENCE_NOTHING_COLOR,
} from 'features/trend-detection/components/issue-constants'
import { IssueAgentMetaRow } from 'features/trend-detection/components/IssueAgentMetaRow'
import IssueFeedbackModal from 'features/trend-detection/components/IssueFeedbackModal'
import {
  DetectedIssueIcon,
  EmergenceBarChart,
  SafetyIndicator,
} from 'features/trend-detection/components/IssueShared'
import {
  type ChartMode,
  IssueTimeseriesChart,
} from 'features/trend-detection/components/IssueTimeseriesChart'
import {
  ChartBarSkeleton,
  ChartContainer,
  ChartLoadingContainer,
  NoChartData,
} from 'features/trend-detection/components/SimpleChart'
import { TrackEmailOptInModal } from 'features/trend-detection/components/track-email-opt-in-modal'
import { useIssueFeedback } from 'features/trend-detection/components/use-issue-feedback'
import {
  patchUpdateDetailSourceIssue,
  useUpdateDetail,
} from 'features/trend-detection/features/issue-detail/hooks/use-update-detail'
import { useTrackEmailOptInPrompt } from 'features/trend-detection/hooks/use-track-email-opt-in-prompt'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import { trendDetectionFeedPath } from 'features/trend-detection/paths'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type { UpdateDetailResponse } from 'features/trend-detection/types'
import type { IssueBreakdownSelectionValue } from 'features/trend-detection/utils/dimension-mappings'
import { downloadChartAsPng } from 'features/trend-detection/utils/download-chart'
import {
  type EmergenceLevel,
  formatOriginalAnalysisPeriod,
  formatUtcDateFromISO,
  getEmergenceLevel,
} from 'features/trend-detection/utils/helpers'
import type { ProjectState } from 'types/project'

type ActiveEmergenceLevel = Exclude<EmergenceLevel, 'nothing'>

const EMERGENCE_TIERS: TierConfig<ActiveEmergenceLevel>[] = (
  ['slightly_elevated', 'elevated', 'sharply_elevated'] as const
).map((key) => ({
  key,
  label: key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
  color: EMERGENCE_COLORS[key],
  icon: () => <EmergenceBarChart level={key} />,
}))

export const UpdateDetailPage: React.FC = () => {
  const { updateUuid } = useParams<{ updateUuid: string }>()
  const projectId = useProjectStore((s: ProjectState) => s.projectId)
  const navigate = useNavigate()
  const service = useTrendDetectionService()
  const { setBreadcrumbSubtitle } = useTopBar()
  const queryClient = useQueryClient()

  const detail = useUpdateDetail(service, projectId, updateUuid ?? null)
  const { updateItem } = detail
  const update = updateItem?.update ?? null
  const sourceIssue = update?.source_issue ?? null

  const emailPrompt = useTrackEmailOptInPrompt(projectId ?? '')
  // Stable across renders (memoized in the hook); depend on it directly rather
  // than the whole `emailPrompt` object, which is a fresh reference every render.
  const { handleTrackResponse: handleEmailPromptResponse } = emailPrompt

  // Page view — fires once per mount so direct-link visits (email, share) are captured.
  // Gate on loaded updateItem so the event carries the source-issue title and agent context.
  const updateDetailViewFiredRef = useRef(false)
  useEffect(() => {
    if (
      !updateUuid ||
      !updateItem ||
      !sourceIssue ||
      updateDetailViewFiredRef.current
    )
      return
    updateDetailViewFiredRef.current = true
    trackResearchAgents(ResearchAgentsAction.update_detail_view, {
      update_uuid: updateUuid,
      source_issue_uuid: sourceIssue.uuid,
      issue_title: sourceIssue.title,
      is_severe: sourceIssue.is_severe,
      agent_type: ISSUE_DETECTION_AGENT_TYPE,
      agent_uuid: updateItem.agent_uuid,
      agent_name: updateItem.agent_name,
    })
  }, [updateUuid, updateItem, sourceIssue])

  // Breadcrumb — show the source issue title
  useEffect(() => {
    setBreadcrumbSubtitle(sourceIssue?.title ?? null)
    return () => setBreadcrumbSubtitle(null)
  }, [sourceIssue?.title, setBreadcrumbSubtitle])

  // Custom breakdowns
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )
  const customBreakdowns = useMemo(
    () => deriveCustomBreakdowns(defaultFilterList || []),
    [defaultFilterList],
  )

  // Tracking — tracks the source issue, updates query cache
  const [trackingLoading, setTrackingLoading] = useState(false)
  const isTracked = sourceIssue?.is_tracked ?? false

  const handleIssueTrackingChange = useCallback(
    async (nextTracked: boolean) => {
      if (!projectId || !sourceIssue) return
      setTrackingLoading(true)
      trackResearchAgents(
        nextTracked
          ? ResearchAgentsAction.issue_track_click
          : ResearchAgentsAction.issue_untrack_click,
        {
          issue_uuid: sourceIssue.uuid,
          issue_title: sourceIssue.title,
          is_severe: sourceIssue.is_severe,
          agent_type: ISSUE_DETECTION_AGENT_TYPE,
          agent_uuid: updateItem?.agent_uuid ?? null,
          agent_name: updateItem?.agent_name ?? null,
          source: 'update_detail',
        },
      )
      let trackResponse: Awaited<ReturnType<typeof service.trackIssue>> | null =
        null
      try {
        if (nextTracked) {
          trackResponse = await service.trackIssue(projectId, sourceIssue.uuid)
        } else {
          await service.untrackIssue(projectId, sourceIssue.uuid)
          // Tracked feed is now keyed off active-tracking, so untrack removes
          // the issue immediately on next refetch — not "after the next run".
        }
        const flipTrackingCache = (toTracked: boolean): void => {
          queryClient.setQueryData<UpdateDetailResponse | undefined>(
            ['update-detail', projectId, updateUuid],
            (prev) =>
              patchUpdateDetailSourceIssue(prev, { is_tracked: toTracked }),
          )
        }
        flipTrackingCache(nextTracked)
        // Refresh the Tracked tab feed and badge so the change is visible
        // immediately on the issue-feed surface (project-scoped prefix so all
        // paged/sorted variants refetch).
        await Promise.all([
          queryClient.invalidateQueries(['trend-feed-updates', projectId]),
          queryClient.invalidateQueries(['trend-feed-counts', projectId]),
        ])
        if (nextTracked) {
          toast.success('Tracking enabled. Find it in your Tracked feed.')
          if (trackResponse) {
            handleEmailPromptResponse(trackResponse, sourceIssue.uuid)
          }
        } else {
          // Stay on the detail page after untrack — the page just toggles its
          // Track/Untrack state and the toast carries the confirmation. Undo
          // re-tracks in place without any navigation.
          const issueUuidForUndo = sourceIssue.uuid
          const handleUndoUntrack = async (): Promise<void> => {
            flipTrackingCache(true)
            try {
              await service.trackIssue(projectId, issueUuidForUndo)
              await Promise.all([
                queryClient.invalidateQueries([
                  'trend-feed-updates',
                  projectId,
                ]),
                queryClient.invalidateQueries(['trend-feed-counts', projectId]),
              ])
              toast.success('Tracking restored.')
            } catch (cause) {
              handleException(cause, {
                operation: 'agent.issue.tracking.restore',
                disposition: 'failed',
              })

              flipTrackingCache(false)
              toast.error('Could not restore. Try again.')
            }
          }
          toast.success(
            <UndoToastBody
              message="Stopped tracking."
              onUndo={handleUndoUntrack}
            />,
          )
        }
      } catch (cause) {
        handleException(cause, {
          operation: 'agent.issue.tracking.update',
          disposition: 'failed',
        })

        toast.error('Could not update tracking. Try again.')
      } finally {
        setTrackingLoading(false)
      }
    },
    [
      projectId,
      sourceIssue,
      updateItem,
      service,
      queryClient,
      updateUuid,
      handleEmailPromptResponse,
    ],
  )

  // Feedback
  const feedback = useIssueFeedback(service, projectId)

  const handleFeedbackClick = useCallback(
    (issueUuid: string, isPositive: boolean) => {
      trackResearchAgents(ResearchAgentsAction.feedback_modal_open, {
        issue_uuid: issueUuid,
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
        is_positive: isPositive,
        source: 'update_detail',
      })
      feedback.handleFeedbackClick(issueUuid, isPositive)
    },
    [feedback],
  )

  const handleFeedbackSubmit = useCallback(
    async (isPositive: boolean, additionalDetails: string) => {
      try {
        await feedback.handleFeedbackSubmit(isPositive, additionalDetails)
        if (sourceIssue) {
          trackResearchAgents(ResearchAgentsAction.feedback_submit, {
            issue_uuid: sourceIssue.uuid,
            agent_type: ISSUE_DETECTION_AGENT_TYPE,
            is_positive: isPositive,
            has_additional_details: additionalDetails.trim().length > 0,
            source: 'update_detail',
          })
        }
        queryClient.setQueryData<UpdateDetailResponse | undefined>(
          ['update-detail', projectId, updateUuid],
          (prev) =>
            patchUpdateDetailSourceIssue(prev, {
              user_has_submitted_feedback: true,
              user_feedback_is_positive: isPositive,
            }),
        )
        toast.success('Feedback submitted')
      } catch (cause) {
        handleException(cause, {
          operation: 'agent.issue.feedback.submit',
          disposition: 'failed',
        })

        toast.error('Failed to submit feedback')
      }
    },
    [feedback, queryClient, projectId, updateUuid, sourceIssue],
  )

  const handleFeedbackDismiss = useCallback(() => {
    if (sourceIssue && feedback.feedbackIsPositive !== null) {
      trackResearchAgents(ResearchAgentsAction.feedback_modal_dismiss, {
        issue_uuid: sourceIssue.uuid,
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
        is_positive: feedback.feedbackIsPositive,
        source: 'update_detail',
      })
    }
    feedback.handleFeedbackCancel()
  }, [feedback, sourceIssue])

  const handleBreakdownDimensionChange = useCallback(
    (dim: IssueBreakdownSelectionValue) => {
      detail.onBreakdownDimensionChange(dim)
      trackResearchAgents(ResearchAgentsAction.breakdown_dimension_change, {
        dimension: dim,
        source: 'update_detail',
      })
    },
    [detail],
  )

  const [chartMode, setChartMode] = useState<ChartMode>('share')

  // Trend chart download
  const trendChartRef = useRef<HTMLDivElement>(null)
  const handleDownloadTrend = useCallback(async () => {
    if (!trendChartRef.current) return
    await downloadChartAsPng(trendChartRef.current, 'mentions-vs-baseline')
    trackResearchAgents(ResearchAgentsAction.insight_chart_download, {
      chart: 'mentions_trend',
      source: 'update_detail',
    })
  }, [])

  const currentEmergenceLevel = getEmergenceLevel(
    update?.update_data?.impact_rate,
  )

  if (!projectId || !updateUuid) return null

  return (
    <>
      <PageShell>
        <InsightDetailSubHeader
          onBack={() =>
            navigate(trendDetectionFeedPath(projectId), {
              state: { feedSection: 'tracked' },
            })
          }
          backLabel="Back to Tracked"
          upvoteActive={
            !!(
              sourceIssue?.user_has_submitted_feedback &&
              sourceIssue.user_feedback_is_positive === true
            )
          }
          downvoteActive={
            !!(
              sourceIssue?.user_has_submitted_feedback &&
              sourceIssue.user_feedback_is_positive === false
            )
          }
          onUpvote={
            sourceIssue
              ? () => handleFeedbackClick(sourceIssue.uuid, true)
              : undefined
          }
          onDownvote={
            sourceIssue
              ? () => handleFeedbackClick(sourceIssue.uuid, false)
              : undefined
          }
          feedbackDisabled={!sourceIssue}
          isTracked={isTracked}
          onTrackingChange={handleIssueTrackingChange}
          trackingLoading={trackingLoading}
          trackingDisabled={!sourceIssue}
          onShare={() =>
            trackResearchAgents(ResearchAgentsAction.insight_share_click, {
              source: 'update_detail',
            })
          }
        />
        <PageContainer>
          <ContentWrapper>
            {detail.updateLoading && (
              <HeaderSkeleton>
                <Skeleton active paragraph={{ rows: 3 }} />
              </HeaderSkeleton>
            )}

            {detail.updateError && !detail.updateLoading && (
              <ErrorSection>
                Update not found or could not be loaded.
              </ErrorSection>
            )}

            {update && sourceIssue && (
              <>
                <InsightHeader>
                  <DetectionBadgeRow>
                    <DetectionStatusIconBox $level={currentEmergenceLevel}>
                      <DetectedIssueIcon
                        color={EMERGENCE_ICON_COLOR[currentEmergenceLevel]}
                        width={24}
                        height={24}
                      />
                    </DetectionStatusIconBox>
                    <DetectionLabels>
                      <DetectionTitle>Issue Updated</DetectionTitle>
                      <EmergenceBadgesWrap>
                        {currentEmergenceLevel !== 'nothing' && (
                          <EmergenceBarChart level={currentEmergenceLevel} />
                        )}
                        <EmergenceBadgeLabel
                          $color={
                            currentEmergenceLevel !== 'nothing'
                              ? EMERGENCE_COLORS[currentEmergenceLevel]
                              : EMERGENCE_NOTHING_COLOR
                          }
                        >
                          {currentEmergenceLevel === 'nothing'
                            ? 'Not currently elevated'
                            : currentEmergenceLevel
                                .replace(/_/g, ' ')
                                .replace(/^\w/, (c) => c.toUpperCase())}
                        </EmergenceBadgeLabel>
                        {currentEmergenceLevel !== 'nothing' && (
                          <ElevationLevelPopover
                            tiers={EMERGENCE_TIERS}
                            activeTierKey={currentEmergenceLevel}
                            share={update.update_data?.share}
                            comparisonMultiplier={
                              update.update_data?.impact_rate
                            }
                          />
                        )}
                        {sourceIssue.is_severe && <SafetyIndicator />}
                      </EmergenceBadgesWrap>
                    </DetectionLabels>
                  </DetectionBadgeRow>
                  <InsightTitle>
                    {sourceIssue.title || 'Tracked Issue Update'}
                  </InsightTitle>
                  {sourceIssue.issue_data?.description && (
                    <InsightDescription>
                      {sourceIssue.issue_data.description}
                    </InsightDescription>
                  )}
                  <IssueAgentMetaRow
                    projectId={projectId}
                    agentUuid={updateItem?.agent_uuid ?? null}
                    agentName={updateItem?.agent_name ?? null}
                    periodLabel={(() => {
                      const period = formatOriginalAnalysisPeriod(
                        update.source_analysis_start_date ?? null,
                        update.source_analysis_end_date ?? null,
                      )
                      return period ? `Detected ${period}` : null
                    })()}
                    updatedDateLabel={
                      update.create_time
                        ? formatUtcDateFromISO(update.create_time)
                        : null
                    }
                  />
                </InsightHeader>

                <ChangesSinceDetection
                  changes={
                    update.changes_since_detection
                      ? {
                          fromLevel:
                            update.changes_since_detection.from_level ?? null,
                          toLevel: update.changes_since_detection.to_level,
                          directionSinceDetection:
                            update.changes_since_detection
                              .direction_since_detection,
                          pctAboveBaseline:
                            update.changes_since_detection.pct_above_baseline,
                          isBackToBaseline:
                            update.changes_since_detection.is_back_to_baseline,
                        }
                      : null
                  }
                />

                <SectionCard>
                  <CardHeader>
                    <CardTitle>Mentions vs. baseline average</CardTitle>
                    <CardActions>
                      <ChartModeButtonGroup>
                        <ChartModeButton
                          type="button"
                          $active={chartMode === 'share'}
                          $first
                          onClick={() => setChartMode('share')}
                        >
                          Share
                        </ChartModeButton>
                        <ChartModeButton
                          type="button"
                          $active={chartMode === 'count'}
                          onClick={() => setChartMode('count')}
                        >
                          Count
                        </ChartModeButton>
                      </ChartModeButtonGroup>
                      <CardIconButton
                        onClick={handleDownloadTrend}
                        aria-label="Download chart"
                      >
                        <FileDownloadOutlined style={{ fontSize: 14 }} />
                      </CardIconButton>
                    </CardActions>
                  </CardHeader>
                  {detail.timeseriesLoading && (
                    <ChartLoadingContainer>
                      <ChartBarSkeleton />
                    </ChartLoadingContainer>
                  )}
                  {!detail.timeseriesLoading &&
                    detail.timeseriesChart &&
                    detail.timeseriesChart.data?.length > 0 && (
                      <ChartContainer ref={trendChartRef}>
                        <IssueTimeseriesChart
                          issueChart={detail.timeseriesChart}
                          projectTotalChart={detail.projectTotalChart}
                          baselineShare={
                            detail.sourceIssueCard?.prior_period_share
                          }
                          mode={chartMode}
                          height="300px"
                        />
                      </ChartContainer>
                    )}
                  {!detail.timeseriesLoading &&
                    (!detail.timeseriesChart ||
                      !detail.timeseriesChart.data?.length) && (
                      <NoChartData>No timeseries data available</NoChartData>
                    )}
                </SectionCard>

                <BreakdownSection
                  breakdownChart={detail.breakdownChart}
                  breakdownLoading={detail.breakdownLoading}
                  selectedBreakdownDimension={detail.selectedBreakdownDimension}
                  customBreakdowns={customBreakdowns}
                  onBreakdownDimensionChange={handleBreakdownDimensionChange}
                  onDownload={() =>
                    trackResearchAgents(
                      ResearchAgentsAction.insight_chart_download,
                      {
                        chart: 'impact_area_breakdown',
                        source: 'update_detail',
                      },
                    )
                  }
                />

                <VerbatimsSection
                  postTexts={detail.postTexts}
                  verbatimsLoading={detail.verbatimsLoading}
                  projectId={projectId}
                  feedbackFilterHash={update.feedback_filter_hash ?? null}
                  onViewAllRecords={() =>
                    trackResearchAgents(
                      ResearchAgentsAction.view_all_records_click,
                      { source: 'update_detail' },
                    )
                  }
                />
              </>
            )}
          </ContentWrapper>
        </PageContainer>
      </PageShell>

      <IssueFeedbackModal
        isVisible={feedback.feedbackModalVisible}
        isPositive={feedback.feedbackIsPositive}
        onSubmit={handleFeedbackSubmit}
        onCancel={handleFeedbackDismiss}
        isLoading={feedback.submittingFeedback}
      />
      <TrackEmailOptInModal
        isOpen={emailPrompt.isOpen}
        agentName={emailPrompt.agentName}
        loading={emailPrompt.loading}
        onOptIn={emailPrompt.onOptIn}
        onDecline={emailPrompt.onDecline}
      />
    </>
  )
}

// ─── Page-local styled components ───────────────────────────────────────────

const UndoToastBody: React.FC<{ message: string; onUndo: () => void }> = ({
  message,
  onUndo,
}) => (
  <UndoToastRow>
    <span>{message}</span>
    <UndoButton type="button" onClick={onUndo}>
      Undo
    </UndoButton>
  </UndoToastRow>
)

const UndoToastRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`

const UndoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`

const HeaderSkeleton = styled.div`
  padding: 24px;
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
`

const ErrorSection = styled.div`
  padding: 40px 24px;
  text-align: center;
  font-size: 15px;
  color: ${darkBlueFontColor};
  opacity: 0.7;
`

const DetectionStatusIconBox = styled.span<{ $level: EmergenceLevel }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  flex-shrink: 0;
  border-radius: 5px;
  background: ${({ $level }) => EMERGENCE_BOX_BG[$level]};
  border: 0.86px solid ${({ $level }) => EMERGENCE_BOX_BORDER[$level]};
`

const EmergenceBadgesWrap = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EmergenceBadgeLabel = styled.span<{ $color: string }>`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-xs, 12px);
  font-weight: 600;
  line-height: var(--Line-height-text-xs, 16px);
  color: ${(p) => p.$color};
`
